import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  CalendarIcon, 
  TrendingUp,
  TrendingDown,
  Banknote,
  CreditCard,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  Calculator,
  ShoppingCart,
  Building2
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BoardDashboard = () => {
  const [events, setEvents] = useState([]);
  const [takipOdemeleri, setTakipOdemeleri] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  
  // Employee stats
  const [fortisEmployeeCount, setFortisEmployeeCount] = useState(0);
  const [taseronCount, setTaseronCount] = useState(0);
  
  // Department calendar states
  const [activeCalendarTab, setActiveCalendarTab] = useState('muhasebe');
  const [ikEvents, setIkEvents] = useState([]);
  const [muhasebeEvents, setMuhasebeEvents] = useState([]);
  const [satinAlmaEvents, setSatinAlmaEvents] = useState([]);
  const [subeEvents, setSubeEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch employee counts from HR employees endpoint
      try {
        const employeesRes = await fetch(`${API_URL}/api/hr/employees`, { headers });
        if (employeesRes.ok) {
          const employees = await employeesRes.json();
          // Count Fortis employees (no subcontractor_id) vs Taşeron (has subcontractor_id)
          const fortis = employees.filter(e => !e.subcontractor_id || e.subcontractor_id === '').length;
          const taseron = employees.filter(e => e.subcontractor_id && e.subcontractor_id !== '').length;
          setFortisEmployeeCount(fortis);
          setTaseronCount(taseron);
        }
      } catch (err) {
        console.error('Employee fetch error:', err);
      }
      
      // Fetch all calendar data in parallel
      const [eventsRes, takipRes, ikRes, satinAlmaRes, subeRes] = await Promise.all([
        fetch(`${API_URL}/api/accounting/takvim`, { headers }),
        fetch(`${API_URL}/api/accounting/takvim/takip-odemeleri`, { headers }),
        fetch(`${API_URL}/api/hr/calendar-events`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/purchasing/calendar-events`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/branches/calendar-events`, { headers }).catch(() => ({ ok: false }))
      ]);
      
      // Muhasebe events
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data);
        setMuhasebeEvents(data.map(e => ({ ...e, department: 'muhasebe', tamamlandi: e.tamamlandi || false })));
      }
      
      // Takip ödemeleri (also muhasebe)
      if (takipRes.ok) {
        const data = await takipRes.json();
        setTakipOdemeleri(data);
        // Add to muhasebe events
        setMuhasebeEvents(prev => [
          ...prev,
          ...data.map(t => ({ ...t, department: 'muhasebe', tamamlandi: t.odendi || false }))
        ]);
      }
      
      // İK events
      if (ikRes.ok) {
        const data = await ikRes.json();
        setIkEvents(data.map(e => ({ ...e, department: 'ik' })));
      } else {
        // Mock IK events if API not available
        setIkEvents([
          { id: 'ik-1', baslik: 'Personel Toplantısı', tarih: new Date().toISOString(), department: 'ik', tipiLabel: 'Toplantı' },
          { id: 'ik-2', baslik: 'İzin Planlaması', tarih: new Date(Date.now() + 86400000).toISOString(), department: 'ik', tipiLabel: 'Planlama' }
        ]);
      }
      
      // Satın Alma events
      if (satinAlmaRes.ok) {
        const data = await satinAlmaRes.json();
        setSatinAlmaEvents(data.map(e => ({ ...e, department: 'satinalma' })));
      } else {
        // Mock Satın Alma events if API not available
        setSatinAlmaEvents([
          { id: 'sa-1', baslik: 'Tedarikçi Görüşmesi', tarih: new Date().toISOString(), department: 'satinalma', tipiLabel: 'Görüşme' },
          { id: 'sa-2', baslik: 'Sipariş Takibi', tarih: new Date(Date.now() + 172800000).toISOString(), department: 'satinalma', tipiLabel: 'Takip' }
        ]);
      }
      
      // Şube events
      if (subeRes.ok) {
        const data = await subeRes.json();
        setSubeEvents(data.map(e => ({ ...e, department: 'sube' })));
      } else {
        // Mock Şube events if API not available
        setSubeEvents([
          { id: 'sb-1', baslik: 'Şube Denetimi', tarih: new Date().toISOString(), department: 'sube', tipiLabel: 'Denetim' },
          { id: 'sb-2', baslik: 'Personel Eğitimi', tarih: new Date(Date.now() + 259200000).toISOString(), department: 'sube', tipiLabel: 'Eğitim' }
        ]);
      }
    } catch (error) {
      console.error('Etkinlikler yüklenemedi:', error);
      toast.error('Takvim verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Get events based on active tab
  const getActiveTabEvents = () => {
    switch (activeCalendarTab) {
      case 'ik':
        return ikEvents;
      case 'muhasebe':
        return muhasebeEvents;
      case 'satinalma':
        return satinAlmaEvents;
      case 'sube':
        return subeEvents;
      case 'tumu':
        return [...ikEvents, ...muhasebeEvents, ...satinAlmaEvents, ...subeEvents];
      default:
        return muhasebeEvents;
    }
  };

  // Combine events and takip payments
  const allEvents = [
    ...events.map(e => ({
      ...e,
      tamamlandi: e.tamamlandi || false
    })),
    ...takipOdemeleri.map(t => ({
      ...t,
      tamamlandi: t.odendi || false
    }))
  ];

  // Get events for selected date based on active tab
  const activeTabEvents = getActiveTabEvents();
  const selectedDateEvents = activeTabEvents.filter(event => {
    const eventDate = new Date(event.tarih);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  // Check if a date has events for active tab
  const hasEvents = (date) => {
    return activeTabEvents.some(event => {
      const eventDate = new Date(event.tarih);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Get department color
  const getDepartmentColor = (dept) => {
    switch (dept) {
      case 'ik': return 'bg-purple-500';
      case 'muhasebe': return 'bg-emerald-500';
      case 'satinalma': return 'bg-orange-500';
      case 'sube': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getDepartmentLabel = (dept) => {
    switch (dept) {
      case 'ik': return 'İK';
      case 'muhasebe': return 'Muhasebe';
      case 'satinalma': return 'Satın Alma';
      case 'sube': return 'Şube';
      default: return dept;
    }
  };

  // Calculate statistics
  const currentMonthEvents = allEvents.filter(e => {
    const eventDate = new Date(e.tarih);
    return eventDate.getMonth() === currentMonth.getMonth() && 
           eventDate.getFullYear() === currentMonth.getFullYear();
  });

  const pendingAlacak = currentMonthEvents
    .filter(e => e.tipi === 'alacak' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);

  const pendingBorc = currentMonthEvents
    .filter(e => e.tipi === 'borc' && !e.tamamlandi)
    .reduce((sum, e) => sum + (parseFloat(e.miktar) || 0), 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  const getOdemeSekliIcon = (sekil) => {
    switch (sekil) {
      case 'havale_eft': return <Banknote className="h-3 w-3" />;
      case 'cek': return <CreditCard className="h-3 w-3" />;
      case 'nakit': return <Banknote className="h-3 w-3" />;
      default: return <Banknote className="h-3 w-3" />;
    }
  };

  const getOdemeSekliLabel = (sekil) => {
    switch (sekil) {
      case 'havale_eft': return 'Havale/EFT';
      case 'cek': return 'Çek';
      case 'nakit': return 'Nakit';
      default: return sekil;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="board-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-500))]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Genel Bakış</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">Muhasebe takvimi ve özet bilgiler</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevMonth}
            className="h-8 w-8 p-0"
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            className="h-8 px-3 text-xs"
            data-testid="today-btn"
          >
            Bugün
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={nextMonth}
            className="h-8 w-8 p-0"
            data-testid="next-month-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Fortis Çalışanları</CardTitle>
            <Users className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600" data-testid="fortis-employees">
              {fortisEmployeeCount}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Toplam personel</p>
          </CardContent>
        </Card>

        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Taşeron Sayısı</CardTitle>
            <Building2 className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600" data-testid="taseron-count">
              {taseronCount}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Alt yüklenici</p>
          </CardContent>
        </Card>

        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Bu Ay Etkinlik</CardTitle>
            <CalendarIcon className="h-5 w-5 text-indigo-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[hsl(var(--foreground))]" data-testid="total-events">
              {currentMonthEvents.length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Bekleyen Alacak</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="pending-alacak">
              {formatCurrency(pendingAlacak)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Bekleyen Borç</CardTitle>
            <TrendingDown className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600" data-testid="pending-borc">
              {formatCurrency(pendingBorc)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-xl">{getMonthName(currentMonth)}</span>
              </CardTitle>
            </div>
            {/* Department Tabs */}
            <Tabs value={activeCalendarTab} onValueChange={setActiveCalendarTab} className="mt-4">
              <TabsList className="grid grid-cols-5 w-full">
                <TabsTrigger value="tumu" className="text-xs">
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  Tümü
                </TabsTrigger>
                <TabsTrigger value="ik" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  İK ({ikEvents.length})
                </TabsTrigger>
                <TabsTrigger value="muhasebe" className="text-xs">
                  <Calculator className="h-3 w-3 mr-1" />
                  Muhasebe ({muhasebeEvents.length})
                </TabsTrigger>
                <TabsTrigger value="satinalma" className="text-xs">
                  <ShoppingCart className="h-3 w-3 mr-1" />
                  Satın Alma ({satinAlmaEvents.length})
                </TabsTrigger>
                <TabsTrigger value="sube" className="text-xs">
                  <Building2 className="h-3 w-3 mr-1" />
                  Şube ({subeEvents.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              showOutsideDays={true}
              className="rounded-md border w-full"
              classNames={{
                months: "flex flex-col w-full",
                month: "space-y-6 w-full",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-xl font-semibold hidden",
                nav: "hidden",
                nav_button: "hidden",
                nav_button_previous: "hidden",
                nav_button_next: "hidden",
                table: "w-full border-collapse",
                head_row: "flex w-full",
                head_cell: "text-muted-foreground rounded-md flex-1 font-semibold text-base py-3",
                row: "flex w-full mt-2",
                cell: "relative flex-1 p-1 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
                day: "h-14 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded-md flex items-center justify-center text-base",
                day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                day_today: "bg-accent text-accent-foreground font-bold",
                day_outside: "text-muted-foreground opacity-50",
                day_disabled: "text-muted-foreground opacity-50",
                day_hidden: "invisible",
              }}
              modifiers={{
                hasEvent: (date) => hasEvents(date)
              }}
              modifiersStyles={{
                hasEvent: { 
                  fontWeight: 'bold',
                  backgroundColor: '#e0e7ff',
                  borderRadius: '8px'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Selected Day Events */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-indigo-600" />
              {selectedDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Bu tarihte etkinlik yok</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {selectedDateEvents.map((event, index) => (
                  <div
                    key={event.id || index}
                    className={cn(
                      "p-3 rounded-lg border",
                      event.tamamlandi 
                        ? "bg-gray-50 border-gray-200" 
                        : event.tipi === 'alacak' 
                          ? "bg-green-50 border-green-200" 
                          : event.tipi === 'borc'
                            ? "bg-red-50 border-red-200"
                            : "bg-indigo-50 border-indigo-200"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {/* Department Badge */}
                          {event.department && (
                            <Badge className={cn("text-white text-xs px-1.5 py-0", getDepartmentColor(event.department))}>
                              {getDepartmentLabel(event.department)}
                            </Badge>
                          )}
                          {event.tipi === 'alacak' ? (
                            <TrendingUp className={cn("h-4 w-4", event.tamamlandi ? "text-gray-400" : "text-green-600")} />
                          ) : event.tipi === 'borc' ? (
                            <TrendingDown className={cn("h-4 w-4", event.tamamlandi ? "text-gray-400" : "text-red-600")} />
                          ) : null}
                          <span className={cn(
                            "font-semibold",
                            event.tamamlandi ? "text-gray-500 line-through" : 
                            event.tipi === 'alacak' ? "text-green-700" : 
                            event.tipi === 'borc' ? "text-red-700" : "text-indigo-700"
                          )}>
                            {formatCurrency(event.miktar)}
                          </span>
                          {event.is_takip && (
                            <Badge variant="outline" className="text-xs">
                              Takip {event.odeme_sira}. Ödeme
                            </Badge>
                          )}
                          {event.is_cek && (
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              event.cek_tip === 'alinan' ? "border-green-500 text-green-700" : "border-red-500 text-red-700"
                            )}>
                              {event.cek_tip === 'alinan' ? 'Alınan Çek' : 'Verilen Çek'}
                            </Badge>
                          )}
                        </div>
                        {event.cari_hesap_adi && (
                          <p className="text-sm text-gray-600 font-medium">{event.cari_hesap_adi}</p>
                        )}
                        {event.is_cek && (
                          <div className="mt-2 space-y-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            {event.cek_no && (
                              <p><span className="font-medium">Çek No:</span> {event.cek_no}</p>
                            )}
                            {event.banka && (
                              <p><span className="font-medium">Banka:</span> {event.banka}</p>
                            )}
                            {event.kesideci && (
                              <p><span className="font-medium">Keşideci:</span> {event.kesideci}</p>
                            )}
                            {event.alici && (
                              <p><span className="font-medium">Alıcı:</span> {event.alici}</p>
                            )}
                            {event.duzenleme_tarihi && (
                              <p><span className="font-medium">Düzenleme:</span> {new Date(event.duzenleme_tarihi).toLocaleDateString('tr-TR')}</p>
                            )}
                            {event.hesap_karti_adi && (
                              <p><span className="font-medium">Hesap:</span> {event.hesap_karti_adi}</p>
                            )}
                            {event.aciklama && (
                              <p><span className="font-medium">Açıklama:</span> {event.aciklama}</p>
                            )}
                          </div>
                        )}
                        {!event.is_cek && event.odeme_sekli && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            {getOdemeSekliIcon(event.odeme_sekli)}
                            <span>{getOdemeSekliLabel(event.odeme_sekli)}</span>
                          </div>
                        )}
                        {!event.is_cek && event.aciklama && (
                          <p className="text-xs text-gray-500 mt-1">{event.aciklama}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {event.is_cek && (
                          <Badge variant="secondary" className="text-xs">
                            Vade
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BoardDashboard;
